import React, { useState } from 'react'
import Modal from 'react-modal'
import { UilTimes } from '@iconscout/react-unicons'
import ls from '@livesession/browser'

import configurations from '../../helpers/misc/configurations'
import { accesslog } from '../../helpers/global'
import { usersQuickSignUpApi } from '../../apis/users'
import Initializers from '../../dependencies/initializers'

if (typeof document !== 'undefined') {
  Modal.setAppElement('#root')
}

const customStyles = {
  content: {
    position: 'relative',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '460px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    background: '#FFF',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)'
  },
  overlay: {
    backgroundColor: 'rgba(24, 36, 78, 0.6)',
    zIndex: 1000,
    padding: '20px'
  }
}

const initialValues = { companyName: '', companyEmail: '', phoneNumber: '' }

function validate(values) {
  const errors = {}

  if (!values.companyName.trim()) {
    errors.companyName = 'Please enter your company name.'
  } else if (values.companyName.length > 65) {
    errors.companyName = 'Company Name cannot be more than 65 characters.'
  }

  if (!values.companyEmail) {
    errors.companyEmail = 'Please enter your company email.'
  } else if (!configurations.emailRegex.test(values.companyEmail.toLowerCase())) {
    errors.companyEmail = 'Please enter a valid company email.'
  }

  if (values.phoneNumber) {
    const digitsOnly = values.phoneNumber.replace(/\D/g, '')
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      errors.phoneNumber = 'Please enter a valid phone number.'
    }
  }

  return errors
}

const ModalRecommendsSignUp = ({ isOpen, onRequestClose, jobTitle, skill, questionText, questionCode, answerText, assessResult, signUpSource, title, subtext, ctaLabel }) => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const handleChange = (field) => (e) => {
    const { value } = e.target
    setValues((prev) => ({ ...prev, [field]: value }))
  }

  const handleClose = () => {
    onRequestClose()
    // Reset after the close animation/unmount rather than before, so the
    // form doesn't visibly flash empty while the modal is still closing.
    setTimeout(() => {
      setValues(initialValues)
      setErrors({})
      setSubmitError('')
      setIsSuccess(false)
    }, 200)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const validationErrors = validate(values)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    accesslog('astrnt-recommends', 'quick_signup_submit')

    usersQuickSignUpApi({
      company_name: values.companyName,
      email: values.companyEmail,
      mobile_number: values.phoneNumber,
      source: signUpSource,
      job_title: jobTitle || '',
      skill: skill || '',
      // Carried over from the try-questions flow so the post-signup landing can show the
      // already-submitted question and result without asking the user to redo it. skill,
      // question_text and answer_text are all-or-nothing on the backend -- if any is
      // missing the whole carryover is silently dropped (signup still succeeds).
      // question_code isn't part of the documented contract but is included anyway as
      // the interview-question/interview-answer log-correlation id.
      question_text: questionText || '',
      question_code: questionCode || '',
      answer_text: answerText || '',
      result: assessResult || null
    }).then(({ response, error }) => {
      setIsSubmitting(false)

      if (error) {
        const apiMessage = error.response && error.response.data && error.response.data.message
        setSubmitError(apiMessage || 'Something went wrong. Please try again.')
        accesslog('astrnt-recommends', 'quick_signup_error')
        ls.track('signup_failed', { reason: apiMessage || 'api_error' })
        return
      }

      const verificationLink = response.data && response.data.verification_link
      if (!verificationLink) {
        setSubmitError('Something went wrong. Please try again.')
        accesslog('astrnt-recommends', 'quick_signup_error')
        ls.track('signup_failed', { reason: 'missing_verification_link' })
        return
      }

      setIsSuccess(true)
      accesslog('astrnt-recommends', 'quick_signup_success')
      ls.track('signup_completed')
      window.location = verificationLink
    })
  }

  const handleLoginClick = () => {
    accesslog('astrnt-recommends', 'quick_signup_login_click')
    window.location = Initializers.recommends_host + '/login'
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={customStyles}
      contentLabel="Quick Sign Up Modal"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      className="modal-recommends-signup"
    >
      <button
        type="button"
        className="modal-recommends-signup-close"
        onClick={handleClose}
        aria-label="Close modal"
      >
        <UilTimes />
      </button>
      <div className="modal-recommends-signup-body">
        {isSuccess ? (
          <React.Fragment>
            <h3>You're all set!</h3>
            <p>
              Your ASTRNT account has been created. Redirecting you to verify your account...
            </p>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <h3>{title}</h3>
            <p className="modal-recommends-signup-subtext">
              {subtext}
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="recommends-company-name">Company Name <span className="required">*</span></label>
              <input
                id="recommends-company-name"
                type="text"
                placeholder="e.g: Nova Interactive"
                value={values.companyName}
                onChange={handleChange('companyName')}
              />
              {errors.companyName && <span className="field-error">{errors.companyName}</span>}

              <label htmlFor="recommends-company-email">Company Email <span className="required">*</span></label>
              <input
                id="recommends-company-email"
                type="email"
                placeholder="mail@company.com"
                value={values.companyEmail}
                onChange={handleChange('companyEmail')}
              />
              {errors.companyEmail && <span className="field-error">{errors.companyEmail}</span>}

              <label htmlFor="recommends-phone-number">Phone Number</label>
              <input
                id="recommends-phone-number"
                type="tel"
                placeholder="e.g: +6187823189"
                value={values.phoneNumber}
                onChange={handleChange('phoneNumber')}
              />
              {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}

              {submitError && <div className="form-error">{submitError}</div>}

              <button type="submit" className="modal-recommends-signup-submit" disabled={isSubmitting} data-ls-id="signup-submit-button">
                {isSubmitting ? 'Submitting...' : ctaLabel}
              </button>

              <div className="modal-recommends-signup-trust">
                <i className="fas fa-shield-alt" /> No credit card, no commitment.
              </div>

              <div className="modal-recommends-signup-login">
                Already have an account? <a onClick={handleLoginClick} data-ls-id="signup-login-link">Log in</a>
              </div>
            </form>
          </React.Fragment>
        )}
      </div>
    </Modal>
  )
}

ModalRecommendsSignUp.defaultProps = {
  questionText: '',
  questionCode: '',
  answerText: '',
  assessResult: null,
  signUpSource: 'astrnt_recommends',
  title: "You're one step away from your top candidates",
  subtext: 'Fill in a few quick details to unlock full candidate profiles.',
  ctaLabel: 'Unlock All Candidates'
}

export default ModalRecommendsSignUp
